import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { when } from "../accounts";
import { REASON_LABELS, reportStatusLabel, targetLabel } from "../moderation";
import { errorText, unwrap } from "../problem";

type View = "open" | "all";

// Every page is audited (`report.list`), so the queue loads once per visit
// and again only when asked (Refresh, Load more, another view).
export function ModerationPage() {
  const [view, setView] = useState<{ status: View; run: number }>({ status: "open", run: 0 });
  const list = useInfiniteQuery({
    queryKey: ["reports", view],
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/reports", {
          params: { query: { status: view.status, ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Each request is audited: a failure is shown, never retried by itself.
    retry: false,
    refetchOnReconnect: false,
  });

  const reports = list.data?.pages.flatMap((page) => page.reports) ?? [];
  const first = list.data?.pages[0];
  return (
    <>
      <h1>Moderation</h1>
      <p className="muted">
        Reports from the apps. Open a report to read the note and the reported message. Lists, opened reports and
        actions are recorded in the audit log.
      </p>
      <div className="filters">
        <label>
          Show
          <select
            value={view.status}
            onChange={(event) => setView((previous) => ({ status: event.target.value as View, run: previous.run + 1 }))}
          >
            <option value="open">Open reports (oldest first)</option>
            <option value="all">All reports (newest first)</option>
          </select>
        </label>
        <button
          type="button"
          className="button-quiet"
          onClick={() => setView((previous) => ({ ...previous, run: previous.run + 1 }))}
          disabled={list.isFetching}
        >
          Refresh
        </button>
      </div>
      {list.isFetching && !list.isFetchingNextPage && <p>Loading…</p>}
      {list.isError && <p className="error">{errorText(list.error)}</p>}
      {first && (
        <p className="muted">
          {view.status === "open"
            ? `${first.open.toLocaleString()} open ${first.open === 1 ? "report" : "reports"}`
            : `${first.matched.toLocaleString()} ${first.matched === 1 ? "report" : "reports"}, ${first.open.toLocaleString()} open`}
        </p>
      )}
      {first && reports.length === 0 && <p>Nothing to review.</p>}
      {reports.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Reported</th>
              <th>Reason</th>
              <th>About</th>
              <th>Reported account</th>
              <th>By</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr key={report.id}>
                <td>
                  <Link to={`/moderation/${report.id}`}>{when(report.created_at)}</Link>
                </td>
                <td>{REASON_LABELS[report.reason]}</td>
                <td>{report.about_message ? "A message" : "The account"}</td>
                <td>
                  {targetLabel(report.target)}
                  {report.target.suspended && <span className="muted"> (suspended)</span>}
                </td>
                <td>{report.reporter?.name ?? "Deleted account"}</td>
                <td>{reportStatusLabel(report)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list.hasNextPage && (
        <button
          type="button"
          className="button-quiet"
          onClick={() => void list.fetchNextPage()}
          disabled={list.isFetchingNextPage}
        >
          {list.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </>
  );
}
